import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "./router";
import { posts, getPost, formatDate } from "./posts";

function useTitle(title) {
  useEffect(() => {
    document.title = title;
    return () => {
      document.title = "Osmonz Services";
    };
  }, [title]);
}

export function BlogIndex() {
  useTitle("Blog | Osmonz Services");

  return (
    <section className="section-blog">
      <div className="container container--narrow">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-header">
            <p className="eyebrow">Insights</p>
            <h2 className="section-title">Blog</h2>
            <p className="blog-intro">
              Practical thinking on workloading, staffing models, and running a cleaning
              operation that fits the building it serves.
            </p>
          </div>

          <ul className="post-list">
            {posts.map((post) => (
              <li key={post.slug} className="post-list-item">
                <span className="post-date">{formatDate(post.date)}</span>
                <h3 className="post-list-title">
                  <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h3>
                <p className="post-list-excerpt">{post.excerpt}</p>
                <Link to={`/blog/${post.slug}`} className="post-list-link">
                  Read the post
                </Link>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

export function BlogPost({ slug }) {
  const post = getPost(slug);

  useTitle(post ? `${post.title} | Osmonz Services` : "Not found | Osmonz Services");

  if (!post) {
    return (
      <section className="section-blog">
        <div className="container container--narrow">
          <div className="section-header">
            <p className="eyebrow">404</p>
            <h2 className="section-title">That post doesn’t exist</h2>
          </div>
          <Link to="/blog" className="post-back">
            Back to all posts
          </Link>
        </div>
      </section>
    );
  }

  return (
    <article className="section-blog">
      <div className="container container--narrow">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/blog" className="post-back">
            All posts
          </Link>
          <span className="post-date">{formatDate(post.date)}</span>
          <h1 className="post-title">{post.title}</h1>
          {post.subtitle && <p className="post-subtitle">{post.subtitle}</p>}

          <div className="post-body">
            {post.body.map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2 key={i} className="post-h2">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "ul") {
                return (
                  <ul key={i} className="post-bullets">
                    {block.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                );
              }
              return <p key={i}>{block.text}</p>;
            })}
          </div>

          <div className="post-footer">
            <p className="post-footer-text">
              Wondering what this means for your building? We’ll walk through it with you in a
              free 20-minute call.
            </p>
            <a href="mailto:info@osmonzservices.com" className="contact-box-btn">
              Get in Touch
            </a>
          </div>
        </motion.div>
      </div>
    </article>
  );
}
